import { useRef, useState, type MouseEvent } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { useJDDrafts } from "@/api/jd-gap-queries";
import { useJDPolicies, useJDQuestions } from "@/api/jd-question-queries";
import { useJD } from "@/api/jd-queries";
import { useCan } from "@/auth/member";
import { ActivityStep } from "@/components/jd-activity";
import { SourceTextDrawer } from "@/components/jd-details-parts";
import { DetailsStep } from "@/components/jd-details-step";
import { DraftProvider, SaveBar } from "@/components/jd-draft";
import { JDHeader } from "@/components/jd-header";
import { MixStep } from "@/components/jd-mix-step";
import { PublishStep } from "@/components/jd-publish-step";
import { QuestionsStep } from "@/components/jd-questions-step";
import { SkillsStep } from "@/components/jd-skills-step";
import { StepFooter, StepsNav } from "@/components/jd-steps-nav";
import { ErrorNote } from "@/components/ui/panel";
import { useDraft } from "@/lib/jd-draft-state";
import { STEP_KEYS, stepStates, type StepKey } from "@/lib/jd-steps";

/**
 * One job description — DESIGN §20.8, §20.11, redesigned 2026-10-15. Five steps in the order the work
 * happens (Details, Skills, Questions, Interview mix, Preview & publish) and its Activity, each step
 * saying what is left; one save bar for every unsaved edit on the page; one primary action in the
 * header. The step is in the URL, so a link from the gap report or a finished draft job lands on it.
 */

// Links written before the redesign named the old tabs.
const LEGACY_TAB: Record<string, StepKey> = {
  overview: "details",
  skills: "skills",
  questions: "questions",
  policy: "mix",
  history: "activity",
};

function stepFrom(params: URLSearchParams): StepKey {
  const step = params.get("step");
  const found = STEP_KEYS.find((k) => k === step);
  if (found) return found;
  return LEGACY_TAB[params.get("tab") ?? ""] ?? "details";
}

export default function JobDescription() {
  const { id } = useParams();
  const jdId = Number(id);
  // Keyed, so moving to a sibling position starts from that position's own saved state.
  return (
    <DraftProvider key={jdId} jdId={jdId}>
      <JobDescriptionPage jdId={jdId} />
    </DraftProvider>
  );
}

function JobDescriptionPage({ jdId }: { jdId: number }) {
  const draft = useDraft();
  const dirty = draft.count > 0;
  const canManage = useCan("manage_jd");
  const detail = useJD(Number.isFinite(jdId) ? jdId : null);
  const questions = useJDQuestions(jdId);
  const drafts = useJDDrafts(jdId, false);
  const policies = useJDPolicies();
  const [params, setParams] = useSearchParams();
  const step = stepFrom(params);
  const [showText, setShowText] = useState(false);
  const top = useRef<HTMLDivElement>(null);

  // Leaving by an in-page link would drop unsaved edits without the browser's prompt.
  const leave = (e: MouseEvent) => {
    if (dirty && !window.confirm("You have unsaved changes on this job description. Leave without saving?")) {
      e.preventDefault();
    }
  };
  const go = (next: StepKey) => {
    setParams({ step: next }, { replace: true });
    // The app's <main> scrolls, not the window.
    top.current?.scrollIntoView({ block: "start" });
  };

  const jd = detail.data;
  if (detail.isLoading) return <p className="text-bank-muted p-4 text-sm">Loading…</p>;
  if (!jd) {
    return (
      <div className="p-4">
        <ErrorNote error={detail.error ?? "No such job description."} />
      </div>
    );
  }

  const policyId = draft.policyId(jd.jd.policy_id ?? null);
  const fallback = policies.data?.find((p) => p.is_default);
  const policyName =
    policyId === null
      ? (fallback?.name ?? "Default")
      : (policies.data?.find((p) => p.id === policyId)?.name ?? "Policy");
  const states = stepStates({
    jd,
    questions: questions.data,
    drafts: (drafts.data?.batches ?? []).reduce((n, b) => n + b.items.length, 0),
    skills: jd.skills.map(draft.skill),
    counts: draft.questionCounts(jd.jd.question_counts ?? null),
    policyName,
  });

  return (
    <div ref={top} className="flex min-h-full flex-col gap-3 px-5 pt-4">
      <JDHeader jd={jd} dirty={dirty} onLeave={leave} onShowText={() => setShowText(true)} />
      {/* {jd.jd.status === "active" && (
        <p className="border-bank-good bg-bank-good-wash text-bank-good-ink rounded-lg border px-3.5 py-2 text-[13px]">
          <strong>This JD is live.</strong> Saved changes reach members from their next interview; interviews
          already started keep their questions.
        </p>
      )}
      {jd.jd.status === "discarded" && jd.jd.discard_reason && (
        <p className="border-bank-border-strong bg-bank-surface-2 rounded-lg border px-3.5 py-2 text-[13px]">
          Discarded: “{jd.jd.discard_reason}”. Keep it to review it like any other position.
        </p>
      )} */}
      <StepsNav states={states} current={step} onSelect={go} />

      {step === "details" && (
        <DetailsStep
          jd={jd}
          canManage={canManage}
          onShowText={() => setShowText(true)}
          onSeeQuestions={() => go("questions")}
        />
      )}
      {step === "skills" && <SkillsStep jd={jd} canManage={canManage} />}
      {step === "questions" && <QuestionsStep jd={jd} canManage={canManage} />}
      {step === "mix" && <MixStep jd={jd} canManage={canManage} />}
      {step === "publish" && <PublishStep jd={jd} canManage={canManage} dirty={dirty} onGo={go} />}
      {step === "activity" && <ActivityStep jd={jd} />}
      <StepFooter current={step} onSelect={go} />

      <SaveBar published={jd.jd.status === "active"} />
      {canManage && <SourceTextDrawer jd={jd} open={showText} onClose={() => setShowText(false)} />}
    </div>
  );
}
